import { decimalToPoints } from 'utils/decimalUtils';

/**
 * The one place the frontend derives "which finishes does this printing
 * exist in" from a card payload. Every finish control, finish label, and
 * finish-gated sheen reads this instead of re-deriving from `foil` booleans
 * or price presence (Asana 1217914880858819, Decision 10).
 *
 * Two casings exist by design and must never be mixed:
 *   - card-level facts (`finishes`, `availableFinishes`) are Scryfall
 *     lowercase: 'nonfoil' | 'foil' | 'etched'  -> CardFinish
 *   - row-level facts (a have row's stored finish, Track D) are the Prisma
 *     enum: 'NONFOIL' | 'FOIL' | 'ETCHED'      -> HaveFinish
 */
export type CardFinish = 'nonfoil' | 'foil' | 'etched';
export type HaveFinish = 'NONFOIL' | 'FOIL' | 'ETCHED';

/** Canonical order for every finish list, chip row, and D-5 cell. */
export const FINISH_ORDER: readonly CardFinish[] = ['nonfoil', 'foil', 'etched'];

export const FINISH_LABELS: Record<CardFinish, string> = {
  nonfoil: 'Non-foil',
  foil: 'Foil',
  etched: 'Etched',
};

/** The Price column each finish reads. Display only; the fallback chain lives on the backend. */
export const FINISH_PRICE_KEY: Record<CardFinish, 'usd' | 'usdFoil' | 'usdEtched'> = {
  nonfoil: 'usd',
  foil: 'usdFoil',
  etched: 'usdEtched',
};

export const toHaveFinish = (finish: CardFinish): HaveFinish =>
  finish.toUpperCase() as HaveFinish;

export const fromHaveFinish = (finish: HaveFinish): CardFinish =>
  finish.toLowerCase() as CardFinish;

/**
 * Card- or printing-shaped API object; only the finish-relevant fields
 * matter. `finishes` is the canonical source (already on the wire, null on
 * most non-English rows until the Track B backfill); the rest are fallbacks
 * for payloads that predate it.
 */
export interface FinishSource {
  finishes?: unknown;
  availableFinishes?: unknown;
  availableInFoil?: boolean;
  availableInNonFoil?: boolean;
  foil?: boolean | null;
  nonFoil?: boolean;
  price?: unknown;
}

const isCardFinish = (value: unknown): value is CardFinish =>
  value === 'nonfoil' || value === 'foil' || value === 'etched';

/** Filters an unknown array down to known finishes in canonical order. */
const normalizeFinishList = (value: unknown): CardFinish[] | null => {
  if (!Array.isArray(value)) return null;
  const present = new Set(value.filter(isCardFinish));
  if (present.size === 0) return null;
  return FINISH_ORDER.filter((f) => present.has(f));
};

/** First element of a `price` array, or the object itself. */
export const firstPrice = (price: unknown): Record<string, unknown> | undefined => {
  if (Array.isArray(price)) return price.length > 0 ? price[0] : undefined;
  return price && typeof price === 'object' ? (price as Record<string, unknown>) : undefined;
};

const positive = (value: unknown): boolean => value != null && Number(value) > 0;

/**
 * Which finishes a printing exists in, canonical order, never empty.
 *
 * Precedence (highest wins):
 *   1. `finishes`          the Scryfall array, canonical
 *   2. `availableFinishes` the backend's derived form, when a payload carries only that
 *   3. `availableInFoil` / `availableInNonFoil` backend flags (per flag)
 *   4. legacy derivation: raw `foil` / `nonFoil` booleans, overridden by price
 *      presence when any price is > 0. An etched price counts toward FOIL here,
 *      because a payload with no `finishes` array never implies etched.
 *
 * A null or absent `finishes` yields ['nonfoil', 'foil'] and NEVER etched
 * (Decision 10): after Track B null is rare and is a legacy fallback, not a
 * semantic. Matches the backend helper.
 */
export function getAvailableFinishes(source: FinishSource | undefined | null): CardFinish[] {
  const declared =
    normalizeFinishList(source?.finishes) ?? normalizeFinishList(source?.availableFinishes);
  if (declared) return declared;

  const priceData = firstPrice(source?.price);

  let legacyNonFoil = source?.nonFoil !== undefined ? source.nonFoil : true;
  let legacyFoil = source?.foil !== undefined && source?.foil !== null ? source.foil : true;

  if (priceData) {
    const hasNonFoilPrice = positive(priceData.usd);
    const hasFoilPrice = positive(priceData.usdFoil);
    const hasEtchedPrice = positive(priceData.usdEtched);
    if (hasNonFoilPrice || hasFoilPrice || hasEtchedPrice) {
      legacyNonFoil = hasNonFoilPrice;
      legacyFoil = hasFoilPrice || hasEtchedPrice;
    }
  }

  const availableInNonFoil = source?.availableInNonFoil ?? legacyNonFoil;
  const availableInFoil = source?.availableInFoil ?? legacyFoil;

  const result: CardFinish[] = [];
  if (availableInNonFoil) result.push('nonfoil');
  if (availableInFoil) result.push('foil');
  return result.length > 0 ? result : ['nonfoil', 'foil'];
}

/**
 * Points for one finish from a price object, read from that finish's OWN
 * column only. Null when the column is absent or not positive: render "???",
 * never 0, never a client-side fallback to another finish (the cross-finish
 * chain is the backend's, Decision 11).
 */
export function pointsForFinish(price: unknown, finish: CardFinish): number | null {
  const priceData = firstPrice(price);
  if (!priceData) return null;
  const raw = priceData[FINISH_PRICE_KEY[finish]];
  if (!positive(raw)) return null;
  const points = decimalToPoints(raw);
  return points != null && points > 0 ? points : null;
}

/** The Price column a resolved price was read from (backend Track A, field name priceKey). */
export type PriceKey = 'usd' | 'usdFoil' | 'usdEtched';

export const PRICE_KEY_LABELS: Record<PriceKey, string> = {
  usd: 'non-foil',
  usdFoil: 'foil',
  usdEtched: 'etched',
};

/**
 * Marker rule (Eric, 2026-09-09): a price is an estimate when the column it
 * resolved through is outside the row's own-key set. A have row owns exactly
 * one key (its finish); a want row owns the keys of every finish it accepts.
 * Null (no price resolved) is never an estimate; the display shows "???".
 */
export const isEstimatedPriceKey = (
  priceKey: PriceKey | null | undefined,
  ownKeys: readonly PriceKey[],
): priceKey is PriceKey => priceKey != null && !ownKeys.includes(priceKey);

/** A want row's own keys from its accept flags; `acceptEtched` absent follows foil (migration rule). */
export const wantOwnPriceKeys = (want: {
  acceptFoil?: boolean | null;
  acceptNonFoil?: boolean | null;
  acceptEtched?: boolean | null;
}): PriceKey[] => {
  const keys: PriceKey[] = [];
  if (want.acceptNonFoil ?? true) keys.push('usd');
  if (want.acceptFoil ?? true) keys.push('usdFoil');
  if (want.acceptEtched ?? want.acceptFoil ?? true) keys.push('usdEtched');
  return keys;
};

// ---------------------------------------------------------------------------
// Track D: row-level finish, want accept sets, auto-correct adjustments
// ---------------------------------------------------------------------------

/** `finish` first, legacy `foil` as the fallback (deploy-order guard in both directions). */
export const finishFromRow = (row: {
  finish?: HaveFinish | null;
  foil?: boolean | null;
}): CardFinish => {
  if (row.finish) return fromHaveFinish(row.finish);
  return row.foil ? 'foil' : 'nonfoil';
};

/**
 * The two fields every write and every client model carry together during
 * Track D (review ruling, deploy safety): `finish` is authoritative and `foil`
 * is derived. A backend that does not parse `finish` yet still stores the
 * right premium/non-premium split instead of the default `foil: false`.
 */
export const finishFields = (finish: HaveFinish): { finish: HaveFinish; foil: boolean } => ({
  finish,
  foil: finish !== 'NONFOIL',
});

/**
 * A want's accepted finishes in canonical order. `acceptEtched` absent (a
 * pre-Track-D payload) mirrors the migration rule: etched follows foil.
 */
export const wantAcceptedFinishes = (want: {
  acceptNonFoil?: boolean | null;
  acceptFoil?: boolean | null;
  acceptEtched?: boolean | null;
}): CardFinish[] => {
  const nonfoil = want.acceptNonFoil ?? true;
  const foil = want.acceptFoil ?? true;
  const etched = want.acceptEtched ?? foil;
  const out: CardFinish[] = [];
  if (nonfoil) out.push('nonfoil');
  if (foil) out.push('foil');
  if (etched) out.push('etched');
  return out;
};

/**
 * The finish a WANT row's art renders in (Decision 12: the finish being
 * rendered). A want that accepts only premium finishes renders as its single
 * premium finish; anything that accepts non-foil renders plain.
 */
export const wantRenderFinish = (want: {
  acceptNonFoil?: boolean | null;
  acceptFoil?: boolean | null;
  acceptEtched?: boolean | null;
}): CardFinish => {
  const set = wantAcceptedFinishes(want);
  if (set.includes('nonfoil')) return 'nonfoil';
  if (set.length === 1 && set[0] === 'etched') return 'etched';
  return 'foil';
};

/** Finishes a card offers that a want also accepts, canonical order. */
export const offerableFinishes = (
  card: FinishSource | null | undefined,
  want: { acceptNonFoil?: boolean | null; acceptFoil?: boolean | null; acceptEtched?: boolean | null },
): CardFinish[] => {
  const accepted = new Set(wantAcceptedFinishes(want));
  return getAvailableFinishes(card).filter((f) => accepted.has(f));
};

export type FinishAdjustmentReason =
  | 'ONLY_FINISH_AVAILABLE'
  | 'FINISH_NOT_AVAILABLE'
  | 'DEFAULTED_TO_AVAILABLE';

/**
 * The backend's auto-correct report (Decision 9, Design Pass D-7). Have paths
 * carry single values; want paths carry the accepted set as arrays in
 * canonical order. Null when nothing changed.
 */
export interface FinishAdjustment {
  requested: HaveFinish | HaveFinish[];
  stored: HaveFinish | HaveFinish[];
  reason: FinishAdjustmentReason;
}

const labelFinishes = (v: HaveFinish | HaveFinish[]): string =>
  (Array.isArray(v) ? v : [v]).map((f) => FINISH_LABELS[fromHaveFinish(f)]).join(', ') || 'none';

/** One sentence for the toast, the row marker and the import summary (D-7). */
export const describeAdjustment = (adj: FinishAdjustment | null | undefined): string | null => {
  if (!adj) return null;
  const stored = labelFinishes(adj.stored);
  const requested = labelFinishes(adj.requested);
  switch (adj.reason) {
    case 'ONLY_FINISH_AVAILABLE':
      return `Stored as ${stored}: this printing only exists in that finish (you asked for ${requested}).`;
    case 'FINISH_NOT_AVAILABLE':
      return `Accepting ${stored}: this card does not exist in ${requested}.`;
    case 'DEFAULTED_TO_AVAILABLE':
      return `Accepting ${stored}: your finish preference did not fit this card (you asked for ${requested}).`;
    default:
      return `Stored as ${stored} instead of ${requested}.`;
  }
};

/** The Prisma-enum finish of a row or item: `finish` first, legacy `foil` as the fallback. */
export const haveFinishFromRow = (row: { finish?: HaveFinish | null; foil?: boolean | null }): HaveFinish =>
  toHaveFinish(finishFromRow(row));

/**
 * The finish source for a staged copy: the chosen SetDetail's finish facts when it
 * carries any, else the want row's card. (SetDetail has no price data, so the
 * legacy price derivation only ever runs on the card.)
 */
export const stagedFinishSource = (
  setDetail: { availableFinishes?: string[]; availableInFoil?: boolean; availableInNonFoil?: boolean } | undefined,
  card: FinishSource | undefined | null,
): FinishSource => {
  if (setDetail?.availableFinishes) return { finishes: setDetail.availableFinishes };
  if (setDetail && (setDetail.availableInFoil !== undefined || setDetail.availableInNonFoil !== undefined)) {
    return { availableInFoil: setDetail.availableInFoil, availableInNonFoil: setDetail.availableInNonFoil };
  }
  return card ?? {};
};

/**
 * Default finish for a copy staged against a want: the first finish (canonical
 * order, so non-foil when possible) the printing has AND the want accepts; when
 * the want accepts none of them, the printing's own first finish.
 */
export const defaultStagedFinish = (
  source: FinishSource | undefined | null,
  want: { acceptNonFoil?: boolean | null; acceptFoil?: boolean | null; acceptEtched?: boolean | null },
): HaveFinish => {
  const offerable = offerableFinishes(source, want);
  return toHaveFinish(offerable[0] ?? getAvailableFinishes(source)[0]);
};

/** Trade-surface label: the finish word for a premium copy, the existing dash for non-foil. */
export const finishLabelOrDash = (row: { finish?: HaveFinish | null; foil?: boolean | null }): string => {
  const f = finishFromRow(row);
  return f === 'nonfoil' ? '\u2014' : FINISH_LABELS[f];
};
